import { api, ApiError } from '../api.ts';
import { el, formatDate, mount, onTap, relativeTime } from '../ui/dom.ts';
import { appMain, ensureAppShell, pageIntro } from '../ui/shell.ts';
import { skeletonRows, stateBlock, toast } from '../ui/feedback.ts';
import { loadSession, state } from '../state.ts';
import { navigate, renderGuard } from '../router.ts';
import { lang, t, type MessageKey } from '../i18n/index.ts';

// Kullanım istatistikleri (yalnız yönetici). Veri /api/stats/admin/* uçlarından
// gelir; paket atamaları dahil asıl yetki kapısı sunucudadır.
//
// Grafik yaklaşımı: tek tonlu yatay çubuklar (büyüklük), saat dağılımı için
// tek serili sütunlar, geri kalanı tablo. Kategori rengi kullanılmaz; kimlik
// satır etiketinden okunur, sayılar metin rengindedir.

interface Bucket { key: string; seconds: number; sessions: number }
interface DayBucket { day: string; seconds: number; sessions: number; users: number }
interface PublicEvent {
  id: number; at: number; userId: string | null; email: string | null; deviceKind: string; deviceName: string;
  playbackId: string | null; type: string; kind: string | null; itemKey: string | null; title: string | null;
  quality: number | null; value: number | null; data: Record<string, unknown>;
}
interface Overview {
  range: { from: number; to: number };
  users: { total: number; new: number; active: number };
  watch: { seconds: number; sessions: number; avgSeconds: number; bytes: number };
  byKind: Bucket[]; byQuality: Bucket[]; byDevice: Bucket[]; byPlan: Bucket[];
  byDay: DayBucket[];
  byHour: Array<{ hour: number; seconds: number; sessions: number }>;
  eventCounts: Array<{ type: string; count: number }>;
  topContent: Array<{ itemKey: string; title: string; kind: string; seconds: number; sessions: number; users: number }>;
  endReasons: Array<{ reason: string; count: number }>;
  nowPlaying: Array<{
    playbackId: string; userId: string; email: string; title: string; kind: string; quality: number | null;
    deviceKind: string; deviceName: string; startedAt: number; chargedSeconds: number;
  }>;
}
interface BillingReport {
  subscriptions: {
    paying: number; trial: number; unpaid: number; cancelled: number; pendingChange: number; test: number;
    byPlan: Array<{ plan: string; period: string; count: number; test: number }>;
  };
  mrrLabel: string; mrrTestLabel: string; collectedLabel: string; collectedTestLabel: string; hasTest: boolean; collectedCount: number;
  failedPayments: number; cancellations: number; newSubscriptions: number; planChanges: number;
  payments: Array<{ at: number; userId: string | null; email: string | null; kind: string; plan: string | null; amountLabel: string; test: boolean }>;
}
interface UserSummary {
  id: string; email: string; displayName: string; plan: string; createdAt: number; lastLoginAt: number | null;
  lastSeenAt: number | null; watchSeconds: number; sessions: number; devices: number; events: number;
}
interface UserDetail {
  user: { id: string; email: string; displayName: string; plan: string; createdAt: number; lastLoginAt: number | null; lastSeenAt: number | null; renewsAt: number | null };
  range: { from: number; to: number };
  watch: { seconds: number; sessions: number; avgSeconds: number; bytes: number };
  allTimeSeconds: number;
  byKind: Bucket[]; byQuality: Bucket[]; byDevice: Bucket[]; byDay: DayBucket[];
  endReasons: Array<{ reason: string; count: number }>;
  devices: Array<{ id: string; name: string; kind: string; createdAt: number; lastSeenAt: number }>;
  sessions: PublicEvent[];
  events: PublicEvent[];
  eventCounts: Array<{ type: string; count: number }>;
}

const RANGES: Array<{ key: string; days: number; labelKey: MessageKey }> = [
  { key: 'today', days: 1, labelKey: 'admin.range.today' },
  { key: '7', days: 7, labelKey: 'admin.range.7' },
  { key: '30', days: 30, labelKey: 'admin.range.30' },
  { key: '90', days: 90, labelKey: 'admin.range.90' },
  { key: '365', days: 365, labelKey: 'admin.range.365' },
];

function wrap(...children: Array<Node | null>): HTMLElement {
  return el('div', { class: 'wrap' }, ...children);
}

function daysOf(query?: URLSearchParams): number {
  const key = query?.get('range') ?? '30';
  return RANGES.find((r) => r.key === key)?.days ?? 30;
}

// --- Biçimlendirme ------------------------------------------------------------

function fmtSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return t('admin.unit.seconds', { s });
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return t('admin.unit.hours', { h, m });
  return t('admin.unit.minutes', { m });
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString(lang() === 'en' ? 'en-GB' : 'tr-TR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function fmtNum(n: number): string {
  return n.toLocaleString(lang() === 'en' ? 'en-US' : 'tr-TR');
}

/** Sözlükte karşılığı olmayan anahtar olduğu gibi gösterilir (yeni olay türü
 *  eklendiğinde ekran kırılmaz). */
function label(prefix: 'admin.kind' | 'admin.event' | 'admin.reason', key: string | null | undefined): string {
  const k = `${prefix}.${key ?? 'unknown'}` as MessageKey;
  const translated = t(k);
  return translated === k ? (key ?? '—') : translated;
}

function deviceLabel(kind: string): string {
  const k = `device.${kind}` as MessageKey;
  const translated = t(k);
  return translated === k ? (kind || t('admin.kind.unknown')) : translated;
}

function qualityLabel(q: number | null | string): string {
  if (q === null || q === undefined || q === 'source') return t('admin.quality.source');
  return typeof q === 'number' ? `${q}p` : q;
}

function planLabel(code: string): string {
  if (code === 'free') return t('plans.name.free');
  if (code === 'starter') return t('plans.name.starter');
  if (code === 'plus') return t('plans.name.plus');
  if (code === 'deleted') return t('admin.deleted');
  return code;
}

// --- Parçalar -----------------------------------------------------------------

function kpi(labelText: string, value: string): HTMLElement {
  return el('div', { class: 'kpi' }, el('b', {}, value), el('span', {}, labelText));
}

function bars(title: string, items: Bucket[], nameOf: (key: string) => string): HTMLElement {
  const max = Math.max(1, ...items.map((i) => i.seconds));
  const list = items.length
    ? el('div', { class: 'bars' }, ...items.map((i) => {
        const fill = el('div', { class: 'fill' });
        fill.style.width = `${Math.max(1, Math.round((i.seconds / max) * 100))}%`;
        return el('div', { class: 'bar', title: `${nameOf(i.key)} · ${fmtSeconds(i.seconds)} · ${i.sessions}` },
          el('span', { class: 'lbl' }, nameOf(i.key)),
          el('div', { class: 'track' }, fill),
          el('span', { class: 'val' }, `${fmtSeconds(i.seconds)} · ${i.sessions}`),
        );
      }))
    : el('p', { class: 'inline-note' }, t('admin.empty'));
  return el('section', {}, el('h2', {}, title), list);
}

function table(head: Array<{ label: string; num?: boolean }>, rows: HTMLElement[]): HTMLElement {
  if (!rows.length) return el('p', { class: 'inline-note' }, t('admin.empty'));
  return el('div', { class: 'table-wrap' }, el('table', { class: 'table' },
    el('thead', {}, el('tr', {}, ...head.map((h) => el('th', { class: h.num ? 'num' : '' }, h.label)))),
    el('tbody', {}, ...rows),
  ));
}

function td(text: string, opts: { num?: boolean; muted?: boolean; wrap?: boolean } = {}): HTMLElement {
  const cls = [opts.num ? 'num' : '', opts.muted ? 'muted' : '', opts.wrap ? 'wrap-text' : ''].filter(Boolean).join(' ');
  return el('td', { class: cls }, text);
}

function hourChart(items: Array<{ hour: number; seconds: number }>): HTMLElement {
  const max = Math.max(1, ...items.map((i) => i.seconds));
  return el('div', {},
    el('div', { class: 'hours' }, ...items.map((i) => {
      const bar = el('i', { title: `${String(i.hour).padStart(2, '0')}:00 · ${fmtSeconds(i.seconds)}` });
      bar.style.height = `${Math.max(2, Math.round((i.seconds / max) * 100))}%`;
      return bar;
    })),
    el('div', { class: 'hours-labels' }, ...items.map((i) => el('span', {}, String(i.hour).padStart(2, '0')))),
  );
}

function rangeChips(active: number, onPick: (key: string) => void): HTMLElement {
  return el('div', { class: 'chip-row range', style: { margin: '0' } }, ...RANGES.map((r) => {
    const chip = el('button', { class: 'chip' + (r.days === active ? ' active' : ''), type: 'button' }, t(r.labelKey));
    onTap(chip, () => onPick(r.key));
    return chip;
  }));
}

function eventDetail(e: PublicEvent): string {
  const d = e.data ?? {};
  const parts: string[] = [];
  switch (e.type) {
    case 'view': parts.push(String(d.path ?? '')); break;
    case 'play_end':
      parts.push(fmtSeconds(e.value ?? 0));
      if (d.reason) parts.push(label('admin.reason', String(d.reason)));
      if (Number(d.stalls) > 0) parts.push(`${t('admin.col.stalls')} ${d.stalls}`);
      if (Number(d.netClient) > 0) parts.push(t('admin.net.clientCount', { n: Number(d.netClient) }));
      if (Number(d.netSource) > 0) parts.push(t('admin.net.sourceCount', { n: Number(d.netSource) }));
      break;
    case 'search': parts.push(`“${String(d.q ?? '')}”`, `${e.value ?? 0}`); break;
    case 'quality_change': parts.push(`${qualityLabel(Number(d.from) || null)} → ${qualityLabel(e.quality)}`); break;
    case 'seek': parts.push(`${Math.round(Number(d.from) || 0)} → ${Math.round(e.value ?? 0)} sn`); break;
    case 'skip': parts.push(`${(e.value ?? 0) > 0 ? '+' : ''}${Math.round(e.value ?? 0)} sn`); break;
    case 'pause': case 'resume': parts.push(`${Math.round(e.value ?? 0)} sn`); break;
    case 'stall': parts.push(`${Math.round(e.value ?? 0)} ms`); break;
    case 'net_slow':
      parts.push(t(d.cause === 'client' ? 'admin.net.client' : 'admin.net.source'));
      if (Number(d.stalls) > 0) parts.push(`${t('admin.col.stalls')} ${d.stalls}`);
      if (Number(d.gateMs) > 0) parts.push(t('admin.net.gate', { n: Math.round(Number(d.gateMs) / 1000) }));
      if (Number(d.rxKbps) > 0) parts.push(`${d.rxKbps} kbit/s`);
      if (Number(d.backlogKb) > 0) parts.push(t('admin.net.backlog', { n: Number(d.backlogKb) }));
      if (d.effectiveType) parts.push(String(d.effectiveType));
      break;
    case 'fullscreen': parts.push(d.on ? 'on' : 'off'); break;
    case 'purchase': case 'checkout_start': parts.push(planLabel(e.kind ?? ''), `${((e.value ?? 0) / 100).toFixed(2)} TL`); break;
    case 'admin_plan_changed': parts.push(`${planLabel(String(d.from ?? ''))} → ${planLabel(String(d.to ?? e.kind ?? ''))}`, String(d.actorEmail ?? '')); break;
    case 'player_error': case 'play_error': parts.push(String(d.message ?? '')); break;
    default:
      if (e.kind && !e.title) parts.push(label('admin.kind', e.kind));
  }
  if (e.title && e.type !== 'play_end') parts.unshift(e.title);
  else if (e.title) parts.unshift(e.title);
  return parts.filter(Boolean).join(' · ');
}

function eventRows(events: PublicEvent[], showUser: boolean): HTMLElement[] {
  return events.map((e) => {
    const row = el('tr', { class: showUser && e.userId ? 'link' : '' },
      td(fmtDateTime(e.at), { muted: true }),
      showUser ? td(e.email ?? t('admin.deleted'), { muted: !e.email }) : null,
      td(label('admin.event', e.type)),
      td(eventDetail(e), { wrap: true }),
      td(e.deviceKind ? `${deviceLabel(e.deviceKind)}${e.deviceName ? ` · ${e.deviceName}` : ''}` : '—', { muted: true }),
    );
    if (showUser && e.userId) onTap(row, () => navigate(`/admin/users/${e.userId}`));
    return row;
  });
}

function eventHead(showUser: boolean) {
  return [
    { label: t('admin.col.time') },
    ...(showUser ? [{ label: t('admin.col.user') }] : []),
    { label: t('admin.col.event') },
    { label: t('admin.col.detail') },
    { label: t('admin.col.device') },
  ];
}

function failBlock(err: unknown): HTMLElement {
  const forbidden = err instanceof ApiError && err.status === 403;
  return stateBlock({
    title: forbidden ? t('admin.forbidden') : t('admin.loadFail'),
    body: forbidden ? '' : (err as Error).message,
    action: { label: t('common.goDiscover'), onClick: () => navigate('/browse') },
  });
}

// --- Genel bakış ----------------------------------------------------------------

export async function adminView(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const days = daysOf(query);
  const pick = (key: string) => navigate(`/admin?range=${key}`, true);

  mount(host, wrap(pageIntro(t('admin.title'), rangeChips(days, pick), t('admin.sub')), skeletonRows(4)));

  let data: Overview;
  let users: { users: UserSummary[]; total: number };
  let feed: { events: PublicEvent[]; nextBefore: number | null };
  let billing: BillingReport;
  // Yanıt gelene dek başka ekrana geçildiyse o ekranı ezme.
  const stale = renderGuard();
  try {
    [data, users, feed, billing] = await Promise.all([
      api.get<Overview>(`/api/stats/admin/overview?days=${days}`),
      api.get<{ users: UserSummary[]; total: number }>(`/api/stats/admin/users?days=${days}&limit=50`),
      api.get<{ events: PublicEvent[]; nextBefore: number | null }>(`/api/stats/admin/events?days=${days}&limit=60`),
      api.get<BillingReport>(`/api/stats/admin/billing?days=${days}`),
    ]);
  } catch (err) {
    if (stale()) return;
    mount(host, wrap(pageIntro(t('admin.title')), failBlock(err)));
    return;
  }
  if (stale()) return;

  const nowPlaying = el('section', {},
    el('h2', {}, t('admin.nowPlaying')),
    data.nowPlaying.length
      ? table(
          [{ label: t('admin.col.user') }, { label: t('admin.col.title') }, { label: t('admin.col.kind') },
            { label: t('admin.col.quality') }, { label: t('admin.col.device') }, { label: t('admin.col.started') },
            { label: t('admin.col.watch'), num: true }],
          data.nowPlaying.map((p) => {
            const row = el('tr', { class: 'link' },
              td(p.email), td(p.title, { wrap: true }), td(label('admin.kind', p.kind)), td(qualityLabel(p.quality)),
              td(`${deviceLabel(p.deviceKind)} · ${p.deviceName}`, { muted: true }), td(fmtDateTime(p.startedAt), { muted: true }),
              td(fmtSeconds(p.chargedSeconds), { num: true }),
            );
            onTap(row, () => navigate(`/admin/users/${p.userId}`));
            return row;
          }),
        )
      : el('p', { class: 'inline-note' }, t('admin.nowPlayingEmpty')),
  );

  const usersSection = usersTable(users, days);

  const feedBody = el('tbody', {}, ...eventRows(feed.events, true));
  const more = el('button', { class: 'btn btn-outline btn-sm', type: 'button', style: { marginTop: '12px' } }, t('admin.loadMore'));
  more.hidden = feed.nextBefore === null;
  let nextBefore = feed.nextBefore;
  onTap(more, async () => {
    if (nextBefore === null) return;
    more.disabled = true;
    try {
      const page = await api.get<{ events: PublicEvent[]; nextBefore: number | null }>(
        `/api/stats/admin/events?days=${days}&limit=100&before=${nextBefore}`);
      feedBody.append(...eventRows(page.events, true));
      nextBefore = page.nextBefore;
      more.hidden = nextBefore === null;
    } finally { more.disabled = false; }
  });
  const feedSection = el('section', {},
    el('h2', {}, t('admin.events')),
    feed.events.length
      ? el('div', { class: 'table-wrap' }, el('table', { class: 'table' },
          el('thead', {}, el('tr', {}, ...eventHead(true).map((h) => el('th', {}, h.label)))),
          feedBody))
      : el('p', { class: 'inline-note' }, t('admin.empty')),
    more,
  );

  mount(host, wrap(
    pageIntro(t('admin.title'), rangeChips(days, pick), t('admin.sub')),
    el('div', { class: 'admin' },
      el('div', { class: 'kpis' },
        kpi(t('admin.kpi.activeUsers'), fmtNum(data.users.active)),
        kpi(t('admin.kpi.totalUsers'), fmtNum(data.users.total)),
        kpi(t('admin.kpi.newUsers'), fmtNum(data.users.new)),
        kpi(t('admin.kpi.watchTime'), fmtSeconds(data.watch.seconds)),
        kpi(t('admin.kpi.sessions'), fmtNum(data.watch.sessions)),
        kpi(t('admin.kpi.avgSession'), fmtSeconds(data.watch.avgSeconds)),
        kpi(t('admin.kpi.bytes'), fmtBytes(data.watch.bytes)),
      ),
      billingSection(billing),
      nowPlaying,
      el('div', { class: 'admin-grid' },
        bars(t('admin.byKind'), data.byKind, (k) => label('admin.kind', k)),
        bars(t('admin.byQuality'), data.byQuality, (k) => qualityLabel(k)),
        bars(t('admin.byDevice'), data.byDevice, (k) => deviceLabel(k)),
        bars(t('admin.byPlan'), data.byPlan, (k) => planLabel(k)),
      ),
      el('section', {}, el('h2', {}, t('admin.byHour')), hourChart(data.byHour)),
      el('section', {},
        el('h2', {}, t('admin.byDay')),
        table(
          [{ label: t('admin.col.day') }, { label: t('admin.col.watch'), num: true }, { label: t('admin.col.sessions'), num: true }, { label: t('admin.col.users'), num: true }],
          [...data.byDay].reverse().map((d) => el('tr', {}, td(d.day), td(fmtSeconds(d.seconds), { num: true }), td(fmtNum(d.sessions), { num: true }), td(fmtNum(d.users), { num: true }))),
        ),
      ),
      el('section', {},
        el('h2', {}, t('admin.topContent')),
        table(
          [{ label: t('admin.col.title') }, { label: t('admin.col.kind') }, { label: t('admin.col.watch'), num: true }, { label: t('admin.col.sessions'), num: true }, { label: t('admin.col.users'), num: true }],
          data.topContent.map((c) => el('tr', {}, td(c.title, { wrap: true }), td(label('admin.kind', c.kind), { muted: true }),
            td(fmtSeconds(c.seconds), { num: true }), td(fmtNum(c.sessions), { num: true }), td(fmtNum(c.users), { num: true }))),
        ),
      ),
      el('div', { class: 'admin-grid' },
        el('section', {},
          el('h2', {}, t('admin.endReasons')),
          table([{ label: t('admin.col.reason') }, { label: t('admin.col.count'), num: true }],
            data.endReasons.map((r) => el('tr', {}, td(label('admin.reason', r.reason)), td(fmtNum(r.count), { num: true })))),
        ),
        el('section', {},
          el('h2', {}, t('admin.eventCounts')),
          table([{ label: t('admin.col.event') }, { label: t('admin.col.count'), num: true }],
            data.eventCounts.map((r) => el('tr', {}, td(label('admin.event', r.type)), td(fmtNum(r.count), { num: true })))),
        ),
      ),
      usersSection,
      feedSection,
    ),
  ));
}

/** Abonelikler ve gelir. Deneme ortamındaki tutarlar gelir değildir: ayrı gösterilir. */
function billingSection(b: BillingReport): HTMLElement {
  const s = b.subscriptions;
  const testNote = b.hasTest
    ? el('p', { class: 'inline-note', style: { display: 'block', marginBottom: '12px' } },
        el('span', { class: 'tag-soft tag-test' }, 'TEST'), ' ',
        t('admin.billing.testNote', { n: s.test, mrr: b.mrrTestLabel, collected: b.collectedTestLabel }))
    : null;
  const kindLabel = (kind: string) => t(`admin.billing.kind.${kind}` as MessageKey);
  return el('section', {},
    el('h2', {}, t('admin.billing.title')),
    testNote,
    el('div', { class: 'kpis' },
      kpi(t('admin.billing.paying'), fmtNum(s.paying)),
      kpi(t('admin.billing.trial'), fmtNum(s.trial)),
      kpi(t('admin.billing.unpaid'), fmtNum(s.unpaid)),
      kpi(t('admin.billing.cancelled'), fmtNum(s.cancelled)),
      kpi(t('admin.billing.mrr'), b.mrrLabel),
      kpi(t('admin.billing.collected'), b.collectedLabel),
      kpi(t('admin.billing.failed'), fmtNum(b.failedPayments)),
      kpi(t('admin.billing.cancellations'), fmtNum(b.cancellations)),
    ),
    s.byPlan.length
      ? table(
          [{ label: t('admin.col.plan') }, { label: t('admin.billing.col.period') }, { label: t('admin.billing.col.subs'), num: true }, { label: t('admin.billing.col.test'), num: true }],
          s.byPlan.map((p) => el('tr', {}, td(planLabel(p.plan)), td(p.period === 'yearly' ? t('bill.yearly') : t('bill.monthly')), td(fmtNum(p.count), { num: true }), td(fmtNum(p.test), { num: true, muted: true }))),
        )
      : null,
    el('h3', { style: { fontSize: '14px', fontWeight: '600', margin: '20px 0 8px' } }, t('admin.billing.payments')),
    b.payments.length
      ? table(
          [{ label: t('admin.col.time') }, { label: t('admin.col.user') }, { label: t('admin.billing.col.kind') }, { label: t('admin.col.plan') }, { label: t('admin.billing.col.amount'), num: true }],
          b.payments.map((p) => {
            const row = el('tr', p.userId ? { class: 'link' } : {},
              td(fmtDateTime(p.at), { muted: true }),
              td(p.email ?? t('admin.billing.deletedAccount'), { muted: !p.email }),
              td(kindLabel(p.kind)),
              td(p.plan ? planLabel(p.plan) : '—'),
              td((p.test ? 'TEST · ' : '') + (p.amountLabel || '—'), { num: true }),
            );
            if (p.userId) onTap(row, () => navigate(`/admin/users/${p.userId}`));
            return row;
          }),
        )
      : el('p', { class: 'inline-note' }, t('admin.billing.none')),
  );
}

function usersTable(initial: { users: UserSummary[]; total: number }, days: number): HTMLElement {
  const body = el('tbody', {});
  const total = el('span', { class: 'inline-note' }, t('admin.users.total', { n: initial.total }));
  const render = (list: UserSummary[]) => {
    mount(body, ...list.map((u) => {
      const row = el('tr', { class: 'link' },
        el('td', {}, el('a', { href: `/admin/users/${u.id}` }, u.email)), td(planLabel(u.plan), { muted: true }),
        td(fmtSeconds(u.watchSeconds), { num: true }), td(fmtNum(u.sessions), { num: true }), td(fmtNum(u.devices), { num: true }),
        td(relativeTime(u.lastSeenAt), { muted: true }), td(formatDate(u.createdAt), { muted: true }),
      );
      onTap(row, () => navigate(`/admin/users/${u.id}`));
      return row;
    }));
  };
  render(initial.users);

  const input = el('input', { type: 'search', placeholder: t('admin.users.search'), 'aria-label': t('admin.users.search') });
  let timer: number | null = null;
  input.addEventListener('input', () => {
    if (timer) clearTimeout(timer);
    timer = window.setTimeout(async () => {
      try {
        const q = encodeURIComponent(input.value.trim());
        const page = await api.get<{ users: UserSummary[]; total: number }>(`/api/stats/admin/users?days=${days}&limit=50&q=${q}`);
        render(page.users);
        total.textContent = t('admin.users.total', { n: page.total });
      } catch { /* arama başarısız; liste olduğu gibi kalır */ }
    }, 250);
  });

  return el('section', {},
    el('h2', {}, t('admin.users')),
    el('p', { class: 'inline-note' }, t('admin.plan.listHint')),
    el('div', { class: 'search-box' }, input, total),
    el('div', { class: 'table-wrap' }, el('table', { class: 'table' },
      el('thead', {}, el('tr', {},
        el('th', {}, t('admin.col.user')), el('th', {}, t('admin.col.plan')),
        el('th', { class: 'num' }, t('admin.col.watch')), el('th', { class: 'num' }, t('admin.col.sessions')),
        el('th', { class: 'num' }, t('admin.col.devices')), el('th', {}, t('admin.col.lastSeen')), el('th', {}, t('admin.col.created')),
      )),
      body,
    )),
  );
}

// --- Kullanıcı ayrıntısı ---------------------------------------------------------

function planEditor(user: UserDetail['user'], onSaved: () => Promise<void>): HTMLElement {
  const select = el('select', { id: 'admin-user-plan' },
    ...['free', 'starter', 'plus'].map((code) => el('option', { value: code }, planLabel(code))),
  );
  select.value = user.plan;
  const save = el('button', { class: 'btn btn-light', type: 'submit' }, t('admin.plan.save'));
  const message = el('p', { role: 'status', 'aria-live': 'polite', class: 'inline-note' });
  const current = t('admin.plan.current', { plan: planLabel(user.plan) }) +
    (user.plan !== 'free' ? ` · ${user.renewsAt ? t('admin.plan.ends', { date: formatDate(user.renewsAt) }) : t('admin.plan.noExpiry')}` : '');
  const form = el('form', { class: 'admin-plan-form' },
    el('label', { htmlFor: select.id }, t('admin.col.plan')),
    el('div', { class: 'row-actions' }, select, save),
    message,
  );
  let busy = false;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    busy = true;
    select.disabled = save.disabled = true;
    message.className = 'inline-note';
    message.textContent = t('common.loading');
    try {
      await api.post(`/api/stats/admin/users/${encodeURIComponent(user.id)}/plan`, { planCode: select.value });
      toast(t('admin.plan.saved', { plan: planLabel(select.value) }), 'ok');
      if (state.session?.user.id === user.id) await loadSession();
      if (form.isConnected) await onSaved();
    } catch (err) {
      message.className = 'error-note';
      message.textContent = (err as Error).message;
    } finally {
      busy = false;
      select.disabled = save.disabled = false;
    }
  });
  return el('section', { class: 'admin-plan' },
    el('h2', {}, t('admin.plan.title')),
    el('p', {}, current),
    el('p', { class: 'inline-note' }, t('admin.plan.hint')),
    form,
  );
}

export async function adminUserView(params: Record<string, string>, query?: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const days = daysOf(query);
  const pick = (key: string) => navigate(`/admin/users/${params.id}?range=${key}`, true);
  const back = el('button', { class: 'btn btn-quiet btn-sm', type: 'button' }, t('admin.user.back'));
  onTap(back, () => navigate('/admin'));

  mount(host, wrap(pageIntro(t('admin.title'), rangeChips(days, pick)), skeletonRows(3)));

  let d: UserDetail;
  const stale = renderGuard();
  try {
    d = await api.get<UserDetail>(`/api/stats/admin/users/${encodeURIComponent(params.id)}?days=${days}`);
  } catch (err) {
    if (stale()) return;
    mount(host, wrap(pageIntro(t('admin.title')), failBlock(err)));
    return;
  }
  if (stale()) return;

  mount(host, wrap(
    pageIntro(d.user.displayName ? `${d.user.displayName} · ${d.user.email}` : d.user.email, rangeChips(days, pick),
      `${planLabel(d.user.plan)} · ${t('admin.user.created')}: ${formatDate(d.user.createdAt)} · ${t('admin.col.lastSeen')}: ${relativeTime(d.user.lastSeenAt)}`),
    el('div', { style: { marginBottom: '20px' } }, back),
    el('div', { class: 'admin' },
      planEditor(d.user, () => adminUserView(params, query)),
      el('div', { class: 'kpis' },
        kpi(t('admin.kpi.watchTime'), fmtSeconds(d.watch.seconds)),
        kpi(t('admin.kpi.sessions'), fmtNum(d.watch.sessions)),
        kpi(t('admin.kpi.avgSession'), fmtSeconds(d.watch.avgSeconds)),
        kpi(t('admin.kpi.bytes'), fmtBytes(d.watch.bytes)),
        kpi(t('admin.kpi.allTime'), fmtSeconds(d.allTimeSeconds)),
      ),
      el('div', { class: 'admin-grid' },
        bars(t('admin.byKind'), d.byKind, (k) => label('admin.kind', k)),
        bars(t('admin.byQuality'), d.byQuality, (k) => qualityLabel(k)),
        bars(t('admin.byDevice'), d.byDevice, (k) => deviceLabel(k)),
      ),
      el('section', {},
        el('h2', {}, t('admin.user.devices')),
        table(
          [{ label: t('admin.col.device') }, { label: t('admin.col.kind') }, { label: t('admin.col.lastSeen') }, { label: t('admin.col.created') }],
          d.devices.map((dev) => el('tr', {}, td(dev.name), td(deviceLabel(dev.kind), { muted: true }), td(relativeTime(dev.lastSeenAt), { muted: true }), td(formatDate(dev.createdAt), { muted: true }))),
        ),
      ),
      el('section', {},
        el('h2', {}, t('admin.byDay')),
        table(
          [{ label: t('admin.col.day') }, { label: t('admin.col.watch'), num: true }, { label: t('admin.col.sessions'), num: true }],
          [...d.byDay].reverse().map((day) => el('tr', {}, td(day.day), td(fmtSeconds(day.seconds), { num: true }), td(fmtNum(day.sessions), { num: true }))),
        ),
      ),
      el('section', {},
        el('h2', {}, t('admin.user.sessions')),
        table(
          [{ label: t('admin.col.time') }, { label: t('admin.col.title') }, { label: t('admin.col.kind') }, { label: t('admin.col.quality') },
            { label: t('admin.col.device') }, { label: t('admin.col.watch'), num: true }, { label: t('admin.col.stalls'), num: true }, { label: t('admin.col.reason') }],
          d.sessions.map((s) => el('tr', {},
            td(fmtDateTime(s.at), { muted: true }), td(s.title ?? '—', { wrap: true }), td(label('admin.kind', s.kind), { muted: true }),
            td(qualityLabel(s.quality)), td(`${deviceLabel(s.deviceKind)} · ${s.deviceName}`, { muted: true }),
            td(fmtSeconds(s.value ?? 0), { num: true }), td(fmtNum(Number(s.data.stalls) || 0), { num: true }),
            td(label('admin.reason', String(s.data.reason ?? 'unknown')), { muted: true }),
          )),
        ),
      ),
      el('div', { class: 'admin-grid' },
        el('section', {},
          el('h2', {}, t('admin.endReasons')),
          table([{ label: t('admin.col.reason') }, { label: t('admin.col.count'), num: true }],
            d.endReasons.map((r) => el('tr', {}, td(label('admin.reason', r.reason)), td(fmtNum(r.count), { num: true })))),
        ),
        el('section', {},
          el('h2', {}, t('admin.eventCounts')),
          table([{ label: t('admin.col.event') }, { label: t('admin.col.count'), num: true }],
            d.eventCounts.map((r) => el('tr', {}, td(label('admin.event', r.type)), td(fmtNum(r.count), { num: true })))),
        ),
      ),
      el('section', {},
        el('h2', {}, t('admin.user.events')),
        table(eventHead(false), eventRows(d.events, false)),
      ),
    ),
  ));
}
